import { Separator } from '@/components/ui/separator'
import { MetricCardWithDelta } from './MetricCardWithDelta'
import { AttendantRankingCard } from './AttendantRankingCard'
import { MessageHistogramChart } from './MessageHistogramChart'
import { AttendantMetricsCard, AttendantMetricsTable } from '@/components/attendance-metrics'
import { Clock, MessageSquare, Users } from 'lucide-react'
import type { SnapshotDashboardData } from '@/types/attendance-metrics'

interface SnapshotDashboardProps {
  snapshotData: SnapshotDashboardData
  comparisonData?: SnapshotDashboardData | null
}

function formatTime(seconds: number | null): string {
  if (seconds === null) return 'N/A'
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  return `${(seconds / 60).toFixed(1)}min`
}

function calculateDelta(current: number, comparison: number): number {
  if (comparison === 0) return 0
  return ((current - comparison) / comparison) * 100
}

export function SnapshotDashboard({ snapshotData, comparisonData }: SnapshotDashboardProps) {
  const { kpis, rankings, histogram, attendantMetrics } = snapshotData

  // Calculate deltas if comparison is available
  const frtDelta = comparisonData
    ? {
        value: calculateDelta(kpis.frtAvg || 0, comparisonData.kpis.frtAvg || 0),
        isImprovement: (kpis.frtAvg || 0) < (comparisonData.kpis.frtAvg || 0), // Lower is better
      }
    : undefined

  const tmrDelta = comparisonData
    ? {
        value: calculateDelta(kpis.tmrAvg || 0, comparisonData.kpis.tmrAvg || 0),
        isImprovement: (kpis.tmrAvg || 0) < (comparisonData.kpis.tmrAvg || 0), // Lower is better
      }
    : undefined

  const messagesDelta = comparisonData
    ? {
        value: calculateDelta(kpis.totalMessages, comparisonData.kpis.totalMessages),
        isImprovement: kpis.totalMessages > comparisonData.kpis.totalMessages, // Higher is better
      }
    : undefined

  const responsesDelta = comparisonData
    ? {
        value: calculateDelta(kpis.totalResponses, comparisonData.kpis.totalResponses),
        isImprovement: kpis.totalResponses > comparisonData.kpis.totalResponses, // Higher is better
      }
    : undefined

  const ticketsDelta = comparisonData
    ? {
        value: calculateDelta(kpis.totalTickets, comparisonData.kpis.totalTickets),
        isImprovement: kpis.totalTickets > comparisonData.kpis.totalTickets, // Higher is better
      }
    : undefined

  // Convert snapshot rankings to component format
  const rankingsData = {
    byMessages: rankings.byMessages.map((r) => ({
      attendantOdataId: r.userId.toString(),
      attendantName: r.userName,
      value: r.count,
    })),
    byResponses: rankings.byResponses.map((r) => ({
      attendantOdataId: r.userId.toString(),
      attendantName: r.userName,
      value: r.count,
    })),
    byTickets: rankings.byTickets.map((r) => ({
      attendantOdataId: r.userId.toString(),
      attendantName: r.userName,
      value: r.count,
    })),
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <MetricCardWithDelta
          title="FRT Médio"
          value={formatTime(kpis.frtAvg)}
          description="First Response Time"
          icon={Clock}
          delta={frtDelta}
        />
        <MetricCardWithDelta
          title="TMR Médio"
          value={formatTime(kpis.tmrAvg)}
          description="Tempo Médio de Resposta"
          icon={Clock}
          delta={tmrDelta}
        />
        <MetricCardWithDelta
          title="Mensagens"
          value={kpis.totalMessages.toLocaleString()}
          description="Total de mensagens"
          icon={MessageSquare}
          delta={messagesDelta}
        />
        <MetricCardWithDelta
          title="Respostas"
          value={kpis.totalResponses.toLocaleString()}
          description="Grupos de respostas"
          icon={MessageSquare}
          delta={responsesDelta}
        />
        <MetricCardWithDelta
          title="Tickets"
          value={kpis.totalTickets.toLocaleString()}
          description="Total de atendimentos"
          icon={Users}
          delta={ticketsDelta}
        />
      </div>

      {/* Rankings */}
      <AttendantRankingCard rankings={rankingsData} />

      {/* Message Volume Chart */}
      <MessageHistogramChart
        data={histogram.buckets.map((b) => ({ timestamp: b.timestamp, inbound: b.count, outbound: 0 }))}
        interval={histogram.interval}
      />

      <Separator />

      {/* Attendant Metrics */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Por Atendente</h2>

        {/* Mobile: Cards */}
        <div className="grid gap-3 md:hidden">
          {attendantMetrics.map((attendant) => (
            <AttendantMetricsCard
              key={attendant.userId}
              name={attendant.userName}
              odataId={attendant.userId}
              frt={attendant.frtAvg}
              tmr={attendant.tmrAvg}
              messageCount={attendant.totalMessages}
              responseCount={attendant.totalResponses}
              ticketCount={attendant.totalTickets}
            />
          ))}
        </div>

        {/* Desktop: Table */}
        <div className="hidden md:block">
          <AttendantMetricsTable
            data={attendantMetrics.map((a) => ({
              attendantOdataId: a.userId.toString(),
              attendantName: a.userName,
              frtAvg: a.frtAvg,
              tmrAvg: a.tmrAvg,
              totalMessages: a.totalMessages,
              totalResponses: a.totalResponses,
              totalTickets: a.totalTickets,
            }))}
            isLoading={false}
          />
        </div>
      </div>
    </div>
  )
}
